import {ConsumerComplaintPatientModel} from "@/models/consumer_complaint/ConsumerComplaintPatientModel.js";
import {ConsumerComplaintHealthPlanModel} from "@/models/consumer_complaint/ConsumerComplaintHealthPlanModel.js";
import {ConsumerComplaintAuthorizedRepModel} from "@/models/consumer_complaint/ConsumerComplaintAuthorizedRepModel.js";
import {ConsumerComplaintProviderModel} from "@/models/consumer_complaint/ConsumerComplaintProviderModel.js";
import {ConsumerComplaintModel} from "@/models/consumer_complaint/ConsumerComplaintModel.js";
import {ConsumerComplaintDocumentModel} from "@/models/consumer_complaint/ConsumerComplaintDocumentModel.js";
import axios from "axios";
import {checkToken} from "@/utils/lambdaUtils.js";
import {useAuthStore} from "@/stores/authStore.js";
import {ExternalAppealModel} from "@/models/external_appeals/ExternalAppealModel.js";
import logger from "@/utils/logger.js";

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL + '/consumer_complaint/';

export const submitComplaint = async (formData) => {
    try {
        // Instantiate nested models first
        const patient = new ConsumerComplaintPatientModel({
            firstName: formData.patient.firstName,
            middleName: formData.patient.middleName || null,
            lastName: formData.patient.lastName,
            dob: formData.patient.dob,
            phoneNumber: formData.patient.phoneNumber,
            email: formData.patient.email,
            street: formData.patient.street,
            city: formData.patient.city,
            state: formData.patient.state,
            zipCode: formData.patient.zipCode,
            relationshipToPatient: formData.patient.relationshipToPatient
        });

        const healthPlan = new ConsumerComplaintHealthPlanModel({
            healthInsuranceCompany: formData.healthPlan.healthInsuranceCompany,
            otherHmoName: formData.healthPlan.otherHmoName || null,
            patientInsuranceId: formData.healthPlan.patientInsuranceId,
            typeOfCoverage: formData.healthPlan.typeOfCoverage || null,
            otherTypeOfCoverage: formData.healthPlan.otherTypeOfCoverage
        });

        const documents = formData.documents.map(doc =>
            new ConsumerComplaintDocumentModel({
                type: doc.type,
                url: doc.url
            })
        );

        const authorizedRep = formData.authorizedRep
            ? new ConsumerComplaintAuthorizedRepModel({
                fullName: formData.authorizedRep.fullName,
                phoneNumber: formData.authorizedRep.phoneNumber,
                email: formData.authorizedRep.email
            })
            : null;

        const providers = Array.isArray(formData.providers)
            ? formData.providers.map(p => new ConsumerComplaintProviderModel({
                name: p.name,
                phoneNumber: p.phoneNumber,
                serviceDateFrom: p.serviceDateFrom,
                serviceDateTo: p.serviceDateTo
            }))
            : [];

        const complaintTypes = Array.isArray(formData.complaintTypes)
            ? formData.complaintTypes.map(t => ({
                complaintType: t.complaintType,
                manualComplaintExplanation: t.manualComplaintExplanation || null // if user selects somethingElse
            }))
            : [];

        // Now build the main complaint model, linking nested models
        const complaint = new ConsumerComplaintModel({
            complaintStatus: 'Pending Information Insurance',
            summaryOfComplaint: formData.summaryOfComplaint,
            doNotSendCopyToHmo: formData.doNotSendCopyToHmo,
            signature: formData.signature,
            signatureDate: Date.now(),
            signatureRelationship: formData.signatureRelationship,

            patient,
            healthPlan,
            documents,
            authorizedRep,
            providers,
            complaintTypes
        });

        // Prepare plain JSON payload for axios (strip methods, keep data)
        const payload = {
            complaint: {
                complaintStatus: complaint.complaintStatus,
                summaryOfComplaint: complaint.summaryOfComplaint,
                doNotSendCopyToHmo: complaint.doNotSendCopyToHmo,
                signature: complaint.signature,
                signatureDate: Date.now(),
                signatureRelationship: complaint.signatureRelationship,
            },
            patient: { ...complaint.patient },
            healthPlan: { ...complaint.healthPlan },
            documents: complaint.documents.map(d => ({ type: d.type, url: d.url })),
            authorizedRep: complaint.authorizedRep ? { ...complaint.authorizedRep } : null,
            providers: complaint.providers.map(p => ({
                name: p.name,
                phoneNumber: p.phoneNumber,
                serviceDateFrom: p.serviceDateFrom,
                serviceDateTo: p.serviceDateTo
            })),
            complaintTypes: complaint.complaintTypes.map(t => ({
                complaintType: t.complaintType,
                manualComplaintExplanation: t.manualComplaintExplanation

            }))
        };

        // POST to API
        const response = await axios.post(
            `${baseUrl}submit_complaint`,
            payload,
            {
                headers: {
                    'Content-Type': 'application/json',
                }
            }
        );

        console.log('complaint submitted:', response.data);
        return response.data;

    } catch (e) {
        console.error('Error submitting complaint:', e);
    }
};

export const getConsumerComplaintById = async (complaintID) => {
    const numId = Number.parseInt(complaintID, 10)
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}${encodeURIComponent(numId)}`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (response.data) {
            return new ConsumerComplaintModel(response.data);
        } else {
            console.log(response);
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting Consumer Complaint', error);
    }
};

export const complaintStatusCheck = async (payload) => {
    return await axios.get(`${baseUrl}status_check`, {
        params: payload
    });
};